import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

export function formatEventTime(startTime: string, endTime: string): string {
  const start = new Date(startTime);
  const end = new Date(endTime);
  const now = new Date();
  const today = now.toDateString();
  const isToday = start.toDateString() === today;
  const timeStr = start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const endStr = end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (isToday) return `Today · ${timeStr} – ${endStr}`;
  return `${start.toLocaleDateString([], { month: 'short', day: 'numeric' })} · ${timeStr}`;
}

export function isHappeningNow(startTime: string, endTime: string): boolean {
  const now = Date.now();
  return now >= new Date(startTime).getTime() && now <= new Date(endTime).getTime();
}

export function isToday(startTime: string): boolean {
  return new Date(startTime).toDateString() === new Date().toDateString();
}

export function SectionCard({
  title,
  icon,
  color,
  count,
  seeAllHref,
  children,
  isEmpty,
  emptyLabel,
}: {
  title: string;
  icon: React.ReactNode;
  color: string;
  count?: number;
  seeAllHref: string;
  children: React.ReactNode;
  isEmpty: boolean;
  emptyLabel?: string;
}) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm flex flex-col overflow-hidden hover:shadow-md transition-shadow duration-300">
      <div className={`px-4 py-3 flex items-center justify-between border-b ${color}`}>
        <div className="flex items-center gap-2">
          {icon}
          <span className="font-semibold text-sm">{title}</span>
          {count !== undefined && count > 0 && (
            <span className="text-xs font-medium bg-white/40 px-1.5 py-0.5 rounded-full">{count}</span>
          )}
        </div>
        <Link
          to={seeAllHref}
          className="flex items-center gap-0.5 text-xs font-medium opacity-70 hover:opacity-100 transition-opacity"
        >
          See all <ChevronRight className="w-3 h-3" />
        </Link>
      </div>
      <div className="flex-1 px-4 py-3">
        {isEmpty ? (
          <p className="text-xs text-gray-400 py-2">{emptyLabel ?? 'Nothing here yet.'}</p>
        ) : (
          children
        )}
      </div>
    </div>
  );
}

export function ItemRow({ label, sub, badge }: { label: string; sub?: string; badge?: string }) {
  return (
    <div className="flex items-start justify-between py-1.5 border-b border-gray-50 last:border-0">
      <div className="min-w-0 flex-1 pr-2">
        <p className="text-sm font-medium text-gray-800 truncate">{label}</p>
        {sub && <p className="text-xs text-gray-400 truncate">{sub}</p>}
      </div>
      {badge && (
        <span className="flex-shrink-0 text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">{badge}</span>
      )}
    </div>
  );
}
